// Re-export des Singleton-Clients aus integrations/supabase/client.ts
// Dies verhindert mehrere GoTrueClient Instanzen im Browser
import { supabase as singletonClient } from "@/integrations/supabase/client";
import { isNativePlatform } from "@/lib/transport/detector";
import type { NearbyPlaceCandidate } from "@/types/nearbyPlaces";
import { getSignedVideoUrl } from "@/utils/getSignedVideoUrl";
import type { SupabaseClient } from "@supabase/supabase-js";

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
export const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

if (!supabaseUrl || !supabaseKey) {
  console.error(
    "CRITICAL: Supabase URL or anon key missing. Supabase client will not function correctly. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables."
  );
  // Consider throwing an error if the app cannot run without Supabase
  // throw new Error('Supabase configuration is missing and is essential for the application.');
}

// Verwende den Singleton-Client statt einen neuen zu erstellen
// Dies verhindert "Multiple GoTrueClient instances detected" Warnungen
export const supabase: SupabaseClient = singletonClient;

// Nur Vite-Dev: Konsole `await __supabase.rpc(...)` (kein global in Prod-Build)
if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as unknown as { __supabase: SupabaseClient }).__supabase = supabase;
}

export const getSupabase = (): SupabaseClient => {
  if (typeof window === "undefined" && supabaseUrl && supabaseKey) {
    console.warn(
      "[supabase.ts] getSupabase() called in a non-browser environment. The Supabase client might not behave as expected."
    );
  }
  if (!supabaseUrl || !supabaseKey) {
    throw new Error(
      "[supabase.ts] Supabase client is not configured. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
    );
  }
  return supabase;
};

export async function checkSubscription() {
  try {
    const { data, error } =
      await supabase.functions.invoke("check-subscription");

    if (error) {
      return {
        subscribed: false,
        error: error.message || "Fehler beim Aufruf der Supabase-Funktion",
        rawError: error,
      };
    }

    if (!data) {
      return {
        subscribed: false,
        error: "Keine Daten in der Antwort erhalten",
        rawData: data,
      };
    }

    // NEUE STRUKTUR: check-subscription Edge Function gibt plan_key statt subscription_tier zurück
    // subscribed wird aus subscription_end berechnet
    const subscriptionEnd = data.subscription_end || null;
    const hasActiveSubscription =
      subscriptionEnd && new Date(subscriptionEnd) > new Date();

    return {
      ...data,
      subscribed: hasActiveSubscription,
      subscription_tier: data.plan_key || null, // plan_key wird als subscription_tier zurückgegeben (Backward Compatibility)
      subscription_end: subscriptionEnd,
    };
  } catch (error) {
    // Only log unexpected errors for debugging
    // console.error('Fehler beim Prüfen des Abonnements:', error);
    return {
      subscribed: false,
      error: error instanceof Error ? error.message : "Unbekannter Fehler",
      rawError: error,
    };
  }
}

// NEUE STRUKTUR (Stufe 4C): plan_key + community_location
export async function createCheckoutSession(
  planKey: string,
  communityLocation: string,
  discountType?: string | null,
  referralCode?: string | null,
  locale?: string | null,
  sessionInviteInstanceId?: string | null
): Promise<{ url?: string; error?: string }>;
// LEGACY: Alte Signatur für Backward Compatibility
export async function createCheckoutSession(
  priceId: string,
  tier: string,
  productId?: string,
  promotionCode?: string,
  productName?: string,
  locale?: string | null
): Promise<{ url?: string; error?: string }>;
// Implementierung
export async function createCheckoutSession(
  planKeyOrPriceId: string,
  communityLocationOrTier: string,
  discountTypeOrProductId?: string | null,
  referralCodeOrPromotionCode?: string | null,
  productNameOrLocale?: string | null,
  locale?: string | null,
  sessionInviteInstanceId?: string | null
): Promise<{ url?: string; error?: string }> {
  try {
    // Prüfe ob neue oder alte Struktur verwendet wird
    // Neue Struktur: plan_key ist einer der validen Keys
    const validPlanKeys = [
      "membership_start",
      "membership_aufbau",
      "membership_etabliert",
      "membership_appzugang", // digitaler App-Zugang, eigener Tarif
      "membership_nbg_free", // Nürnberg 0 € dauerhaft (Stripe-SSOT)
      "access_14d",
      "trial_7d",
    ];
    const isNewStructure = validPlanKeys.includes(planKeyOrPriceId);

    if (isNewStructure) {
      // NEUE STRUKTUR (Stufe 4C)
      const planKey = planKeyOrPriceId;
      const communityLocation = communityLocationOrTier;
      const discountType = discountTypeOrProductId || null;
      const referralCode = referralCodeOrPromotionCode || null;
      // Locale: entweder explizit übergeben oder aus productNameOrLocale (wenn es eine Locale ist)
      const checkoutLocale =
        locale ||
        (productNameOrLocale &&
        (productNameOrLocale === "de" || productNameOrLocale === "en")
          ? productNameOrLocale
          : null);

      console.log(
        `[NEUE STRUKTUR] Initialisiere Checkout für: plan_key=${planKey}, community_location=${communityLocation}`
      );

      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          body: {
            plan_key: planKey,
            community_location: communityLocation,
            discount_type: discountType,
            referral_code: referralCode,
            ...(checkoutLocale && { locale: checkoutLocale }),
            ...(sessionInviteInstanceId && {
              session_invite_instance_id: sessionInviteInstanceId,
            }),
            ...(isNativePlatform() && {
              native_success_url: "com.hustle.dance://payment-success",
            }),
          },
        }
      );

      if (error) {
        console.error("Edge Function Error:", error);
        let message = error.message;
        try {
          const ctx = (
            error as { context?: { json?: () => Promise<{ error?: string }> } }
          ).context;
          if (typeof ctx?.json === "function") {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          }
        } catch {
          // Fallback: message bleibt error.message
        }
        return { error: message };
      }

      if (!data?.url) {
        console.error("Keine URL erhalten:", data);
        return { error: "Keine gültige Checkout-URL erhalten" };
      }

      console.log("Checkout-URL erhalten:", data.url);
      return { url: data.url };
    } else {
      // LEGACY: Alte Struktur
      const priceId = planKeyOrPriceId;
      const tier = communityLocationOrTier;
      const productId = discountTypeOrProductId;
      const promotionCode = referralCodeOrPromotionCode;
      // productNameOrLocale ist in Legacy-Struktur das productName
      const productName = productNameOrLocale || tier;
      // Locale: entweder explizit übergeben oder null
      const checkoutLocale = locale || null;

      console.log(
        `[LEGACY] Initialisiere Checkout für: ${tier}, priceId: ${priceId}`
      );

      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          body: {
            priceId,
            tier,
            promotionCode,
            productId,
            productName,
            ...(checkoutLocale && { locale: checkoutLocale }),
            ...(isNativePlatform() && {
              native_success_url: "com.hustle.dance://payment-success",
            }),
          },
        }
      );

      if (error) {
        console.error("Edge Function Error:", error);
        let message = error.message;
        try {
          const ctx = (
            error as { context?: { json?: () => Promise<{ error?: string }> } }
          ).context;
          if (typeof ctx?.json === "function") {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          }
        } catch {
          // Fallback: message bleibt error.message
        }
        return { error: message };
      }

      if (!data?.url) {
        console.error("Keine URL erhalten:", data);
        return { error: "Keine gültige Checkout-URL erhalten" };
      }

      console.log("Checkout-URL erhalten:", data.url);
      return { url: data.url };
    }
  } catch (error) {
    console.error("Fehler beim Erstellen der Checkout-Session:", error);
    return {
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

export async function createFreeSubscription(
  productName: string,
  productId: string
): Promise<{ error?: string }> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { error: "Benutzer nicht authentifiziert" };
    }

    // Erstelle kostenlose Subscription direkt in der Datenbank
    // NEUE STRUKTUR: Verwende plan_key statt subscription_tier
    // Für kostenlose Subscriptions: subscription_end = null (unbegrenzt)
    // WICHTIG: role wird NICHT mehr in subscribers gesetzt (ist in user_private)
    const { error: insertError } = await supabase.from("subscribers").upsert(
      {
        user_id: user.id,
        email: user.email || "",
        plan_key: null, // Kostenlose Subscriptions haben keinen plan_key
        subscription_end: null, // Kein Enddatum für kostenlose Subscriptions
      },
      { onConflict: "user_id" }
    );

    if (insertError) {
      console.error(
        "Fehler beim Erstellen der kostenlosen Subscription:",
        insertError
      );
      return { error: insertError.message };
    }

    return {};
  } catch (error) {
    console.error("Fehler beim Erstellen der kostenlosen Subscription:", error);
    return {
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

export async function saveUserPostalCodeAndCommunity(
  postalCode: string | null,
  communityId: string | null
): Promise<{
  success: boolean;
  error?: string;
  data?: { postalCode?: string | null; communityId: string | null };
}> {
  try {
    // "International": keine PLZ (Community wird per Name in der Edge Function erkannt)
    const isInternational = postalCode == null && communityId == null;

    // Wenn postalCode übergeben wurde, validieren (nicht bei International)
    if (!isInternational && postalCode !== null && postalCode !== undefined) {
      const trimmedPostalCode = postalCode.trim();
      // Validierung: PLZ muss 5-stellig sein
      if (!/^\d{5}$/.test(trimmedPostalCode)) {
        return {
          success: false,
          error: "Ungültige PLZ (muss 5-stellig sein)",
        };
      }
    }

    // Body: bei International explizit postalCode: null senden
    const body: {
      postalCode?: string | null;
      communityId: string | null;
    } = {
      communityId,
    };
    if (isInternational) {
      body.postalCode = null;
    } else if (
      postalCode !== null &&
      postalCode !== undefined &&
      postalCode.trim().length > 0
    ) {
      body.postalCode = postalCode.trim();
    }

    const { data, error } = await supabase.functions.invoke(
      "set-postal-code-and-community",
      {
        body,
      }
    );

    if (error) {
      console.error(
        "[saveUserPostalCodeAndCommunity] Edge Function Fehler:",
        error
      );
      // Versuche, Edge-Function-Body zu extrahieren (hilft bei 4xx/5xx Debugging)
      let details: string | null = null;
      try {
        const ctx = (error as unknown as { context?: { response?: Response } })
          .context;
        if (ctx?.response) {
          details = await ctx.response.text();
        }
      } catch {
        // ignore
      }
      return {
        success: false,
        error: details ? `${error.message} (${details})` : error.message,
      };
    }

    // Prüfe ob Daten zurückgegeben wurden
    if (data?.success && data?.data) {
      console.log(
        "[saveUserPostalCodeAndCommunity] ✅ Erfolgreich gespeichert:",
        {
          hasPostalCode: !!data.data.postalCode,
          postalCode: data.data.postalCode
            ? data.data.postalCode.substring(0, 2) + "***"
            : "nicht gesetzt",
          hasCommunityId: !!data.data.communityId,
        }
      );
      return {
        success: true,
        data: {
          postalCode: data.data.postalCode || null,
          communityId: data.data.communityId || null,
        },
      };
    }

    // Fallback: Wenn keine Daten zurückgegeben wurden, aber auch kein Fehler
    console.warn(
      "[saveUserPostalCodeAndCommunity] ⚠️ Keine Daten in Response:",
      data
    );
    return {
      success: true, // Optimistisch: Function hat 200 zurückgegeben
    };
  } catch (error) {
    console.error(
      "[saveUserPostalCodeAndCommunity] Unerwarteter Fehler:",
      error
    );
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

export type ResolveNearbyPlacesResult =
  | { success: true; candidates: NearbyPlaceCandidate[] }
  | { success: false; error: string; status?: number };

/**
 * MVP: 2–3 Ortsvorschläge per Photon (serverseitig, Rate-Limit in der Edge Function).
 */
export async function invokeResolveNearbyPlaces(
  lat: number,
  lon: number,
  locale: "de" | "en" = "de"
): Promise<ResolveNearbyPlacesResult> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "resolve-nearby-places",
      {
        body: { lat, lon, locale },
      }
    );

    if (error) {
      let details: string | null = null;
      let status: number | undefined;
      try {
        const ctx = (error as unknown as { context?: { response?: Response } })
          .context;
        if (ctx?.response) {
          status = ctx.response.status;
          details = await ctx.response.text();
        }
      } catch {
        // ignore
      }
      return {
        success: false,
        error: details ? `${error.message} (${details})` : error.message,
        status,
      };
    }

    const payload = data as {
      ok?: boolean;
      candidates?: NearbyPlaceCandidate[];
      error?: string;
    };

    if (payload?.ok === true && Array.isArray(payload.candidates)) {
      return { success: true, candidates: payload.candidates };
    }

    return {
      success: false,
      error:
        typeof payload?.error === "string"
          ? payload.error
          : "Unbekannte Antwort",
      status: 502,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

/**
 * Ortsnamen-Suche (Photon Forward), z. B. „Philadelphia“ — mehrere Treffer zur Auswahl.
 * Nutzt dieselbe Edge Function wie {@link invokeResolveNearbyPlaces}, Body `{ q, locale }`.
 */
export async function invokeSearchPlacesByQuery(
  query: string,
  locale: "de" | "en" = "de",
  options?: { lat?: number; lon?: number }
): Promise<ResolveNearbyPlacesResult> {
  const q = query.trim().replace(/\s+/g, " ");
  if (q.length < 2) {
    return { success: false, error: "Suchbegriff zu kurz." };
  }
  try {
    const body: Record<string, unknown> = { q, locale };
    if (
      options?.lat != null &&
      options.lon != null &&
      Number.isFinite(options.lat) &&
      Number.isFinite(options.lon)
    ) {
      body.lat = options.lat;
      body.lon = options.lon;
    }

    const { data, error } = await supabase.functions.invoke(
      "resolve-nearby-places",
      {
        body,
      }
    );

    if (error) {
      let details: string | null = null;
      let status: number | undefined;
      try {
        const ctx = (error as unknown as { context?: { response?: Response } })
          .context;
        if (ctx?.response) {
          status = ctx.response.status;
          details = await ctx.response.text();
        }
      } catch {
        // ignore
      }
      return {
        success: false,
        error: details ? `${error.message} (${details})` : error.message,
        status,
      };
    }

    const payload = data as {
      ok?: boolean;
      candidates?: NearbyPlaceCandidate[];
      error?: string;
    };

    if (payload?.ok === true && Array.isArray(payload.candidates)) {
      return { success: true, candidates: payload.candidates };
    }

    return {
      success: false,
      error:
        typeof payload?.error === "string"
          ? payload.error
          : "Unbekannte Antwort",
      status: 502,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

export type AssignCommunityFromMapResult =
  | {
      success: true;
      communityId: string;
      communityName: string;
      createdNew: boolean;
    }
  | { success: false; error: string; status?: number };

/**
 * Variante B: Community aus Photon-Kandidat anlegen oder wiederverwenden, user_private setzen.
 */
export async function invokeAssignCommunityFromMapCandidate(
  candidate: NearbyPlaceCandidate,
  locale: "de" | "en" = "de"
): Promise<AssignCommunityFromMapResult> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "assign-community-from-map-candidate",
      {
        body: {
          displayName: candidate.displayName,
          lat: candidate.lat,
          lon: candidate.lon,
          osm_id: candidate.osm_id,
          osm_type: candidate.osm_type,
          source: candidate.source,
          locale,
        },
      }
    );

    if (error) {
      let details: string | null = null;
      let status: number | undefined;
      try {
        const ctx = (error as unknown as { context?: { response?: Response } })
          .context;
        if (ctx?.response) {
          status = ctx.response.status;
          details = await ctx.response.text();
        }
      } catch {
        // ignore
      }
      return {
        success: false,
        error: details ? `${error.message} (${details})` : error.message,
        status,
      };
    }

    const payload = data as {
      ok?: boolean;
      communityId?: string;
      communityName?: string;
      createdNew?: boolean;
      error?: string;
    };

    if (
      payload?.ok === true &&
      typeof payload.communityId === "string" &&
      typeof payload.communityName === "string" &&
      typeof payload.createdNew === "boolean"
    ) {
      return {
        success: true,
        communityId: payload.communityId,
        communityName: payload.communityName,
        createdNew: payload.createdNew,
      };
    }

    return {
      success: false,
      error:
        typeof payload?.error === "string"
          ? payload.error
          : "Unbekannte Antwort",
      status: 502,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Ein unerwarteter Fehler ist aufgetreten",
    };
  }
}

/** Antwort von Edge Function issue-soft-entry-token */
export type IssueSoftEntryTokenResult = {
  eligibility_token: string;
  expires_at: number;
  campaign_id: string;
};

/**
 * Fordert ein kurzlebiges Soft-Entry-Token an (nur mit gültiger Session).
 * Server: SOFT_ENTRY_CAMPAIGN_IDS muss campaign_id enthalten (z. B. qr_v1, nuremberg_free); SOFT_ENTRY_HMAC_SECRET gesetzt.
 */
export async function issueSoftEntryEligibilityToken(
  campaignId: string
): Promise<IssueSoftEntryTokenResult> {
  const { data, error } = await supabase.functions.invoke(
    "issue-soft-entry-token",
    { body: { campaign_id: campaignId } }
  );
  if (error) {
    throw new Error(error.message ?? "issue-soft-entry-token fehlgeschlagen");
  }
  const d = data as Partial<IssueSoftEntryTokenResult>;
  if (
    typeof d?.eligibility_token !== "string" ||
    typeof d?.expires_at !== "number"
  ) {
    throw new Error("Ungültige Antwort von issue-soft-entry-token");
  }
  return d as IssueSoftEntryTokenResult;
}

export type CreateTrialSubscriptionResult = {
  ok?: boolean;
  already_active?: boolean;
  subscription_id?: string;
  stripe_status?: string;
  trial_days?: number;
  error?: string;
};

/**
 * Legt eine Stripe-Subscription mit Trial an (ohne Checkout). Erfordert vorher issueSoftEntryEligibilityToken.
 */
export async function createTrialSubscription(params: {
  plan_key: string;
  community_location?: string;
  campaign_id: string;
  eligibility_token: string;
}): Promise<CreateTrialSubscriptionResult> {
  const { data, error } = await supabase.functions.invoke(
    "create-trial-subscription",
    { body: params }
  );
  if (error) {
    return {
      ok: false,
      error: error.message ?? "create-trial-subscription fehlgeschlagen",
    };
  }
  const d = (data ?? {}) as CreateTrialSubscriptionResult;
  if (typeof d.error === "string" && d.error.length > 0) {
    return { ok: false, error: d.error };
  }
  return d;
}

export type EnsureOnboardingSubscriptionResult = {
  ok?: boolean;
  skipped?: boolean;
  reason?: string;
  already_active?: boolean;
  subscription_id?: string;
  stripe_status?: string;
  plan_key?: string;
  trial_days?: number;
  error?: string;
};

/**
 * Legt nach Onboarding ein Default-Stripe-Abo an (Edge Function, nur wenn Secret-Flag aktiv).
 * Idempotent: bei deaktiviertem Feature kommt skipped/feature_disabled ohne Fehler.
 */
export async function ensureOnboardingDefaultSubscription(): Promise<EnsureOnboardingSubscriptionResult> {
  const { data, error } = await supabase.functions.invoke(
    "ensure-onboarding-subscription",
    { body: {} }
  );
  if (error) {
    return {
      ok: false,
      error: error.message ?? "ensure-onboarding-subscription fehlgeschlagen",
    };
  }
  const d = (data ?? {}) as EnsureOnboardingSubscriptionResult;
  if (typeof d.error === "string" && d.error.length > 0) {
    return { ok: false, error: d.error };
  }
  return d;
}

export async function openCustomerPortal() {
  try {
    const { data, error } = await supabase.functions.invoke("customer-portal");
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Fehler beim Öffnen des Kundenportals:", error);
    throw error;
  }
}

export async function sendWelcomeEmail(
  email: string,
  tier: string,
  name?: string
) {
  try {
    const { data, error } = await supabase.functions.invoke(
      "send-welcome-email",
      {
        body: { email, tier, name },
      }
    );

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Fehler beim Senden der Willkommens-E-Mail:", error);
    throw error;
  }
}

export async function signUp(
  email: string,
  password: string,
  name: string,
  profilePicUrl?: string,
  referralSource?: string,
  locale?: "de" | "en"
) {
  try {
    // Registriere Nutzer (locale wird in E-Mail-Template für DE/EN genutzt)
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          avatar_url: profilePicUrl,
          referral_source: referralSource || "direct",
          pending_ref_code:
            referralSource && referralSource !== "direct"
              ? referralSource.trim().toUpperCase()
              : null,
          ...(locale && { locale }),
        },
      },
    });

    if (authError) throw authError;
    return authData;
  } catch (error) {
    console.error("Fehler bei der Registrierung:", error);
    throw error;
  }
}

export async function checkUserSubscription(userId: string) {
  try {
    // NEUE STRUKTUR: subscription_end ist Source of Truth, subscribed/subscription_tier wurden entfernt
    const { data: rows, error } = await supabase
      .from("subscribers")
      .select("subscription_end, plan_key")
      .eq("user_id", userId)
      .limit(1);
    const data = rows?.[0] ?? null;

    // PGRST116 bedeutet 'No rows found', was in diesem Kontext kein Fehler ist, sondern dass kein Eintrag existiert.
    if (error && error.code !== "PGRST116") {
      console.error(
        "Fehler beim Überprüfen des Abonnements in checkUserSubscription:",
        error
      );
      return { hasActiveSubscription: false, error }; // Fehlerobjekt zurückgeben
    }

    // NEUE STRUKTUR: subscription_end > now() bedeutet aktives Abonnement
    const hasActiveSubscription =
      data?.subscription_end && new Date(data.subscription_end) > new Date();

    return { hasActiveSubscription: !!hasActiveSubscription, error: null };
  } catch (catchError) {
    console.error(
      "Unerwarteter Fehler beim Überprüfen des Abonnements:",
      catchError
    );
    return { hasActiveSubscription: false, error: catchError }; // Fehlerobjekt zurückgeben
  }
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;

  // Aktualisiere das letzte Login-Datum
  if (data?.user) {
    await supabase
      .from("profiles")
      .update({ last_sign_in_at: new Date().toISOString() })
      .eq("id", data.user.id);
  }

  return data;
}

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error("Fehler beim Abmelden:", error);
    throw error;
  }
}

export async function updateProfile(
  userId: string,
  updates: { name?: string; email?: string }
) {
  try {
    // Update Auth Metadata
    if (updates.name) {
      await supabase.auth.updateUser({
        data: { full_name: updates.name },
      });
    }

    // Update Email wenn angegeben
    if (updates.email) {
      await supabase.auth.updateUser({
        email: updates.email,
      });
    }

    return { success: true };
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Profils:", error);
    throw error;
  }
}

export async function uploadProfileImage(userId: string, file: File) {
  try {
    console.log("Starting file upload for user:", userId);

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      throw new Error(
        `Ungültiger Dateityp: ${file.type}. Nur JPG, PNG, GIF oder WebP sind erlaubt`
      );
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error(
        `Die Datei ist zu groß (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximal 5MB erlaubt.`
      );
    }

    // Generate a unique filename
    const fileExt = file.name.split(".").pop()?.toLowerCase();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `public/${fileName}`; // Changed from profile-images/ to public/

    console.log("Uploading file:", {
      fileName,
      filePath,
      type: file.type,
      size: file.size,
    });

    // List buckets to debug
    const { data: buckets, error: bucketsError } =
      await supabase.storage.listBuckets();
    console.log("Available buckets:", buckets);

    if (bucketsError) {
      console.error("Error listing buckets:", bucketsError);
      throw new Error("Fehler beim Zugriff auf den Speicher");
    }

    // Upload the image with content type
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from("avatars") // Changed from 'user-content' to 'avatars'
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: true,
        contentType: file.type,
      });

    console.log("Upload response:", { uploadData, uploadError });

    if (uploadError) {
      if (uploadError.message.includes("bucket")) {
        console.error("Bucket error - attempting to create bucket...");
        // Try to create the bucket if it doesn't exist
        const { error: createBucketError } =
          await supabase.storage.createBucket("avatars", {
            public: true,
            allowedMimeTypes: validTypes,
            fileSizeLimit: 5 * 1024 * 1024, // 5MB
          });

        if (createBucketError) {
          console.error("Failed to create bucket:", createBucketError);
          throw new Error(
            "Konnte den Speicherbereich nicht erstellen. Bitte kontaktiere den Support."
          );
        }

        // Retry upload after creating bucket
        const retryResult = await supabase.storage
          .from("avatars")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: true,
            contentType: file.type,
          });

        if (retryResult.error) {
          throw new Error(
            `Upload fehlgeschlagen: ${retryResult.error.message}`
          );
        }
      } else {
        throw uploadError;
      }
    }

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("avatars").getPublicUrl(filePath);

    console.log("Generated public URL:", publicUrl);

    // Update user profile with new image URL
    const { error: updateError } = await supabase.auth.updateUser({
      data: { avatar_url: publicUrl },
    });

    if (updateError) {
      console.error("Update Error:", updateError);
      throw new Error("Profil konnte nicht aktualisiert werden");
    }

    return { publicUrl };
  } catch (error) {
    if (error instanceof Error) {
      console.error(
        "Fehler beim Hochladen des Profilbilds:",
        error.message,
        error.stack
      );
      throw new Error("Fehler beim Hochladen: " + error.message);
    } else {
      console.error("Fehler beim Hochladen des Profilbilds:", error);
      throw new Error(
        "Ein unerwarteter Fehler ist aufgetreten: " + JSON.stringify(error)
      );
    }
  }
}

export async function saveQuizData(userId: string, quizData: any) {
  try {
    await supabase.auth.updateUser({
      data: {
        quiz_data: quizData,
        quiz_completed_at: new Date().toISOString(),
      },
    });

    return { success: true };
  } catch (error) {
    console.error("Fehler beim Speichern der Quiz-Daten:", error);
    throw error;
  }
}

/**
 * Schnelle Subscription-Prüfung direkt aus der subscribers Tabelle
 * NEUE STRUKTUR: role kommt aus user_private, subscription_end ist Source of Truth
 * Gibt Subscription-Status und Rolle zurück
 */
export type QuickSubscriptionCheckResult = {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  role: "admin" | "terminal" | "user" | "contributor";
  source?: string;
  error?: string;
};

const quickSubscriptionInflight = new Map<
  string,
  Promise<QuickSubscriptionCheckResult>
>();

async function executeQuickSubscriptionCheck(
  userId: string
): Promise<QuickSubscriptionCheckResult> {
  const [
    { data: subscriberData, error: subscriberError },
    { data: userPrivateData, error: userPrivateError },
  ] = await Promise.all([
    supabase
      .from("subscribers")
      .select("subscription_end, plan_key")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("user_private")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle(),
  ]);

  if (subscriberError && subscriberError.code !== "PGRST116") {
    console.warn(
      "Fehler bei quickSubscriptionCheck (subscribers):",
      subscriberError
    );
  }

  if (userPrivateError && userPrivateError.code !== "PGRST116") {
    console.warn(
      "Fehler bei quickSubscriptionCheck (user_private):",
      userPrivateError
    );
  }

  const hasActiveAccess =
    subscriberData?.subscription_end &&
    new Date(subscriberData.subscription_end) > new Date();

  return {
    subscribed: Boolean(hasActiveAccess),
    subscription_tier: subscriberData?.plan_key || null,
    subscription_end: subscriberData?.subscription_end || null,
    role:
      (userPrivateData?.role as
        | "admin"
        | "terminal"
        | "user"
        | "contributor") || "user",
    source: "subscribers_table",
  };
}

export async function quickSubscriptionCheck(
  knownUserId?: string
): Promise<QuickSubscriptionCheckResult> {
  try {
    let userId = knownUserId;
    if (!userId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      userId = user?.id ?? undefined;
    }

    if (!userId) {
      return {
        subscribed: false,
        subscription_tier: null,
        subscription_end: null,
        role: "user",
        source: "fallback_no_user",
      };
    }

    const inflight = quickSubscriptionInflight.get(userId);
    if (inflight) {
      return inflight;
    }

    const promise = executeQuickSubscriptionCheck(userId).finally(() => {
      quickSubscriptionInflight.delete(userId);
    });
    quickSubscriptionInflight.set(userId, promise);
    return promise;
  } catch (error) {
    console.error("Unerwarteter Fehler bei quickSubscriptionCheck:", error);
    return {
      subscribed: false,
      subscription_tier: null,
      subscription_end: null,
      role: "user",
      source: "fallback_exception",
      error: error instanceof Error ? error.message : "Unbekannter Fehler",
    };
  }
}

/**
 * Synchronisiert Subscription-Status mit Stripe
 * Ruft die Edge Function auf, um Stripe-Daten zu prüfen und zu synchronisieren
 */
export async function ensureSubscriptionSync(): Promise<void> {
  try {
    // Rufe die Edge Function auf, die Stripe prüft und synchronisiert
    const { error } = await supabase.functions.invoke("check-subscription");

    if (error) {
      console.warn("Fehler bei ensureSubscriptionSync:", error);
      // Wir werfen keinen Fehler, damit der Flow weiterläuft
    }
  } catch (error) {
    console.error("Unerwarteter Fehler bei ensureSubscriptionSync:", error);
    // Wir werfen keinen Fehler, damit der Flow weiterläuft
  }
}

// Cache für fetchCurrentUserSocialProfileOnce
const socialProfileCache = new Map<
  string,
  {
    data: {
      user_id: string;
      social_dance_name: string | null;
      avatar_path: string | null;
      self_description: string | null;
      creator_code: string | null;
    } | null;
    timestamp: number;
  }
>();
const SOCIAL_PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

export function clearSocialProfileCache(userId: string): void {
  socialProfileCache.delete(userId);
}

/**
 * Lädt das Social Profile eines Users einmalig (mit Cache)
 * NEUE STRUKTUR (Stufe 4): creator_code kommt aus user_private, social_dance_name/avatar_path aus social_profiles
 */
export async function fetchCurrentUserSocialProfileOnce(
  userId: string
): Promise<{
  user_id: string;
  social_dance_name: string | null;
  avatar_path: string | null;
  self_description: string | null;
  creator_code: string | null;
} | null> {
  const now = Date.now();

  // Verwende Cache, wenn vorhanden und noch gültig
  const cached = socialProfileCache.get(userId);
  if (cached && now - cached.timestamp < SOCIAL_PROFILE_CACHE_DURATION) {
    return cached.data;
  }

  try {
    // Lade creator_code aus user_private (NEUE STRUKTUR)
    const { data: userPrivate, error: userPrivateError } = await supabase
      .from("user_private")
      .select("creator_code")
      .eq("user_id", userId)
      .maybeSingle();

    if (userPrivateError && userPrivateError.code !== "PGRST116") {
      console.warn("Fehler beim Laden von user_private:", userPrivateError);
    }

    // Lade social_dance_name, avatar_path und self_description aus social_profiles (noch nicht migriert)
    const { data: socialProfile, error: socialProfileError } = await supabase
      .from("social_profiles")
      .select("user_id, social_dance_name, avatar_path, self_description")
      .eq("user_id", userId)
      .maybeSingle();

    if (socialProfileError && socialProfileError.code !== "PGRST116") {
      // 406 Not Acceptable ist ok - Tabelle könnte RLS-Probleme haben
      const spStatus = (socialProfileError as unknown as { status?: number })
        .status;
      if (spStatus !== 406) {
        console.warn(
          "Fehler beim Laden des Social Profiles:",
          socialProfileError
        );
      }
    }

    // Kombiniere Daten: creator_code aus user_private, Rest aus social_profiles
    const result = {
      user_id: userId,
      social_dance_name: socialProfile?.social_dance_name || null,
      avatar_path: socialProfile?.avatar_path || null,
      self_description: (socialProfile as { self_description?: string | null } | null)
        ?.self_description ?? null,
      creator_code: userPrivate?.creator_code || null,
    };

    // Aktualisiere Cache
    socialProfileCache.set(userId, { data: result, timestamp: now });

    return result;
  } catch (error) {
    console.error("Unerwarteter Fehler beim Laden des Social Profiles:", error);
    // Falls Cache vorhanden, verwende diesen auch bei Fehler
    if (cached) {
      return cached.data;
    }
    return null;
  }
}

/**
 * Erstellt eine signierte URL für eine Supabase Storage Video-URL.
 *
 * Erwartet typischerweise eine URL, die den Bucket-Namen enthält (z. B. "user-figure-videos").
 * Nutzt die Edge Function `get-signed-video` via `getSignedVideoUrl`.
 */
export async function createSignedVideoUrl(
  videoUrl: string,
  options?: { bucket?: string; ttlSeconds?: number }
): Promise<string> {
  if (!videoUrl || typeof videoUrl !== "string") {
    throw new Error("createSignedVideoUrl: videoUrl is required");
  }

  // blob: URLs können/dürfen nicht signiert werden
  if (videoUrl.startsWith("blob:")) return videoUrl;

  // Bereits signierte URLs unverändert lassen
  if (videoUrl.includes("/storage/v1/object/sign/")) return videoUrl;

  // Bucket & Path extrahieren
  const parsed = new URL(
    videoUrl,
    typeof window !== "undefined" ? window.location.origin : "http://localhost"
  );
  const parts = parsed.pathname.split("/").filter(Boolean);

  const bucketCandidates = ["user-figure-videos", "admin"] as const;
  let bucket = options?.bucket;
  let filePath: string | null = null;

  // Standard-Format: /storage/v1/object/public/<bucket>/<path...>
  const publicIdx = parts.indexOf("public");
  if (!bucket && publicIdx !== -1 && parts[publicIdx + 1]) {
    bucket = parts[publicIdx + 1];
    filePath = parts.slice(publicIdx + 2).join("/") || null;
  }

  // Fallback: suche Bucket direkt im Pfad
  if (!bucket) {
    for (const candidate of bucketCandidates) {
      const idx = parts.indexOf(candidate);
      if (idx !== -1) {
        bucket = candidate;
        filePath = parts.slice(idx + 1).join("/") || null;
        break;
      }
    }
  }

  if (!bucket) {
    throw new Error(
      "createSignedVideoUrl: could not determine bucket from URL"
    );
  }
  if (!filePath) {
    throw new Error(
      "createSignedVideoUrl: could not determine filePath from URL"
    );
  }

  let signedUrl = await getSignedVideoUrl(filePath, {
    bucket,
    ttlSeconds: options?.ttlSeconds ?? 3600,
    suppressErrorLog: true,
  });

  // DEV-only Host-Fix für lokalen Supabase Emulator (kong)
  if (import.meta.env.DEV && signedUrl.includes("://kong:8000")) {
    signedUrl = signedUrl.replace("://kong:8000", "://127.0.0.1:54321");
  }

  return signedUrl;
}

// Cache für fetchSubscribersOnce und fetchSocialProfilesOnce
let subscribersCache: Array<{
  user_id: string;
  email: string;
  full_name: string | null;
}> | null = null;
let socialProfilesCache: Array<{
  user_id: string;
  social_dance_name: string | null;
  avatar_path: string | null;
  instagram_handle: string | null;
}> | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

/**
 * Lädt alle Subscriber einmalig (mit Cache)
 * NEUE STRUKTUR: full_name wird aus user_private (first_name + last_name) kombiniert
 * Cache wird für 5 Minuten gespeichert
 */
export async function fetchSubscribersOnce(): Promise<
  Array<{
    user_id: string;
    email: string;
    full_name: string | null;
  }>
> {
  const now = Date.now();

  // Verwende Cache, wenn vorhanden und noch gültig
  if (subscribersCache && now - cacheTimestamp < CACHE_DURATION) {
    return subscribersCache;
  }

  try {
    // NEUE STRUKTUR: Lade email aus subscribers, first_name/last_name aus user_private
    const { data: subscribersData, error: subscribersError } = await supabase
      .from("subscribers")
      .select("user_id, email");

    if (subscribersError) {
      console.error("Fehler beim Laden der Subscriber:", subscribersError);
      // Falls Cache vorhanden, verwende diesen auch bei Fehler
      if (subscribersCache) {
        return subscribersCache;
      }
      throw subscribersError;
    }

    // Lade user_private Daten für alle User
    const userIds = (subscribersData || []).map(s => s.user_id);
    const { data: userPrivateData, error: userPrivateError } = await supabase
      .from("user_private")
      .select("user_id, first_name, last_name")
      .in("user_id", userIds);

    if (userPrivateError && userPrivateError.code !== "PGRST116") {
      console.warn("Fehler beim Laden von user_private:", userPrivateError);
    }

    // Erstelle Map für schnellen Zugriff
    const userPrivateMap = new Map(
      (userPrivateData || []).map(up => [
        up.user_id,
        { first_name: up.first_name, last_name: up.last_name },
      ])
    );

    // Kombiniere Daten: email aus subscribers, full_name aus user_private
    const subscribers = (subscribersData || []).map(sub => {
      const userPrivate = userPrivateMap.get(sub.user_id);
      const fullName =
        userPrivate?.first_name || userPrivate?.last_name
          ? [userPrivate.first_name, userPrivate.last_name]
              .filter(Boolean)
              .join(" ")
          : null;

      return {
        user_id: sub.user_id,
        email: sub.email || "",
        full_name: fullName,
      };
    });

    // Aktualisiere Cache
    subscribersCache = subscribers;
    cacheTimestamp = now;

    return subscribers;
  } catch (error) {
    console.error("Unerwarteter Fehler beim Laden der Subscriber:", error);
    // Falls Cache vorhanden, verwende diesen auch bei Fehler
    if (subscribersCache) {
      return subscribersCache;
    }
    throw error;
  }
}

/**
 * Lädt alle Social Profiles einmalig (mit Cache)
 * Cache wird für 5 Minuten gespeichert
 * Erweitert um avatar_path und instagram_handle (für Partner-Suche / Doppelprüfung)
 */
export async function fetchSocialProfilesOnce(): Promise<
  Array<{
    user_id: string;
    social_dance_name: string | null;
    avatar_path: string | null;
    instagram_handle: string | null;
  }>
> {
  const now = Date.now();

  // Verwende Cache, wenn vorhanden und noch gültig
  if (socialProfilesCache && now - cacheTimestamp < CACHE_DURATION) {
    return socialProfilesCache;
  }

  try {
    const { data, error } = await supabase
      .from("social_profiles")
      .select("user_id, social_dance_name, avatar_path, instagram_handle");

    if (error) {
      console.error("Fehler beim Laden der Social Profiles:", error);
      // Falls Cache vorhanden, verwende diesen auch bei Fehler
      if (socialProfilesCache) {
        return socialProfilesCache;
      }
      throw error;
    }

    const profiles = (data || []).map(profile => ({
      user_id: profile.user_id,
      social_dance_name: profile.social_dance_name || null,
      avatar_path: profile.avatar_path || null,
      instagram_handle: profile.instagram_handle ?? null,
    }));

    // Aktualisiere Cache
    socialProfilesCache = profiles;
    cacheTimestamp = now;

    return profiles;
  } catch (error) {
    console.error("Unerwarteter Fehler beim Laden der Social Profiles:", error);
    // Falls Cache vorhanden, verwende diesen auch bei Fehler
    if (socialProfilesCache) {
      return socialProfilesCache;
    }
    throw error;
  }
}
